import React from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { GROUPS, PRETTY } from "./constants";

function DependencyRow({ name, dep, severity }) {
  const installed = !!dep?.installed;
  const isCritical = severity === "critical";
  const isImportant = severity === "important";
  const indicatorCls = installed
    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
    : isCritical
      ? "bg-red-500/10 text-red-400 border-red-500/30"
      : isImportant
        ? "bg-amber-500/10 text-amber-400 border-amber-500/30"
        : "bg-slate-700/40 text-slate-400 border-slate-600/40";

  return (
    <div
      data-testid={`dep-row-${name}`}
      className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-800 last:border-0"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span
          className={`flex items-center justify-center w-7 h-7 rounded-full border ${indicatorCls}`}
        >
          {installed ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <XCircle className="w-4 h-4" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-200 truncate">
            {PRETTY[name] || name}
          </p>
          {dep?.path && (
            <p className="text-[11px] text-slate-500 font-mono truncate">
              {dep.path}
            </p>
          )}
          {dep?.languages?.length > 0 && (
            <p className="text-[11px] text-slate-500 mt-0.5">
              langs: {dep.languages.join(", ")}
            </p>
          )}
          {dep?.error && (
            <p className="text-[11px] text-red-400 mt-0.5 truncate">
              {dep.error}
            </p>
          )}
        </div>
      </div>
      <div className="text-right shrink-0">
        {dep?.version ? (
          <span className="text-xs font-mono text-slate-300">
            {dep.version}
          </span>
        ) : installed ? (
          <span className="text-xs text-slate-500">instalado</span>
        ) : (
          <span className="text-xs text-red-400 font-medium">faltante</span>
        )}
      </div>
    </div>
  );
}

function GroupCard({ group, dependencies }) {
  const items = group.keys.map((k) => [k, dependencies?.[k]]);
  const missing = items.filter(([, v]) => !v?.installed).length;
  const accent =
    group.severity === "critical"
      ? "border-red-500/20"
      : group.severity === "important"
        ? "border-amber-500/20"
        : "border-slate-800";
  return (
    <div
      data-testid={`dep-group-${group.severity}`}
      className={`bg-slate-900/50 border ${accent} rounded-xl overflow-hidden`}
    >
      <div className="p-5 border-b border-slate-800">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-white">{group.title}</h3>
            <p className="text-xs text-slate-500 mt-1">{group.description}</p>
          </div>
          {missing > 0 ? (
            <span
              data-testid={`group-missing-${group.severity}`}
              className="shrink-0 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-red-500/10 text-red-300 border border-red-500/30"
            >
              {missing} faltante{missing > 1 ? "s" : ""}
            </span>
          ) : (
            <span className="shrink-0 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
              OK
            </span>
          )}
        </div>
      </div>
      <div className="divide-y divide-slate-800">
        {items.map(([k, v]) => (
          <DependencyRow key={k} name={k} dep={v} severity={group.severity} />
        ))}
      </div>
    </div>
  );
}

export function DependenciesSection({ dependencies }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {GROUPS.map((g) => (
        <GroupCard
          key={g.severity}
          group={g}
          dependencies={dependencies}
        />
      ))}
    </div>
  );
}
